import React, { useState } from "react";
import { Eye, EyeOff, CheckCircle } from "lucide-react";
import OtpInput from "../../../shared/inputs/OtpInput";
import WhatsAppBtn from "../../../shared/inputs/WhatsAppBtn";
import { NotificationType } from "../../../../constants/Enums";
import "./styles/Signup.css";
import genericUtility from "../../../../services/utility/GenericUtility";
import UserApiService from "../../../../services/apiServices/UserApiService";
import { fetchProfile } from "../../../../services/thunks/ProfileThunk";
import { closeAuth } from "../../../../dataManagement/uiDb/GeneralDb";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../shared/contexts/NotificationContext";

const Signup: React.FC = () => {
  const { startLoading, stopLoading, triggerNotification } = useNotification();
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [otp, setOtp] = useState("");

  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countdown, setCountdown] = useState(0);

  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const [errors, setErrors] = useState({
    username: "",
    phone: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
    otp: "",
  });
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const validate = (otpAsWell: boolean = false): boolean => {
    let valid = true;
    const newErrors = {
      username: "",
      phone: "",
      password: "",
      confirmPassword: "",
      referralCode: "",
      otp: "",
    };

    // Username validation
    if (!username.trim()) {
      newErrors.username = "Username is required.";
      valid = false;
    } else if (username.length < 3) {
      newErrors.username = "Username must be at least 3 characters.";
      valid = false;
    }

    // Phone validation
    if (!phone.trim()) {
      newErrors.phone = "Phone number is required.";
      valid = false;
    } else if (!/^[0-9]{10}$/.test(phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number.";
      valid = false;
    }

    // Password validation
    if (!password.trim()) {
      newErrors.password = "Password is required.";
      valid = false;
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
      valid = false;
    }

    // Confirm password validation
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please re-enter password.";
      valid = false;
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
      valid = false;
    }

    // otp validation
    if (otpAsWell) {
      if (!otp.trim()) {
        newErrors.otp = "OTP is required.";
        valid = false;
      } else if (otp.length != 6) {
        newErrors.otp = "OTP must be 6 characters.";
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const handleGetOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    // Simulate OTP sent

    const response = await UserApiService.sendOtp(phone).catch((err) => {
      setErrors((prev) => ({
        ...(prev ?? {}),
        otp: "Couldnt send otp due to unknown server error",
      }));
      console.error("Couldnt send otp due to unknown server error:", err);
      return;
    });

    if (!response) {
      setErrors((prev) => ({
        ...(prev ?? {}),
        otp: "Check your internet connection",
      }));
      return;
    } else if (response.contents && response.contents[0].includes("Active")) {
      setOtpSent(true);
      setCountdown(60);
      return;
    } else if (response.contents && response.contents[0].includes("sent")) {
      setOtpSent(true);
      setCountdown(60);
      return;
    }

    setOtpSent(true);
    setCountdown(60);
    setOtp("");
    setErrors((prev) => ({ ...prev, otp: "" }));
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate(true)) return;

    startLoading("Creating your account...");
    try {
      const payload = {
        username: username ?? "",
        phonenumber: phone ?? "",
        password: password ?? "",
        otp: otp ?? "",
        referralCode: referralCode ?? "",
      };

      const response = await UserApiService.signUp(payload).catch((err) => {
        setErrors((prev) => ({
          ...(prev ?? {}),
          password: "Signup failed due to unknown server error",
        }));
        console.error("Signup failed due to unknown error:", err);
        triggerNotification(
          "Signup failed due to unknown server error",
          NotificationType.ERROR
        );
        return;
      });

      if (!response) {
        setErrors((prev) => ({
          ...(prev ?? {}),
          password: "Check your internet connection",
        }));
        triggerNotification(
          "Check your internet connection",
          NotificationType.ERROR
        );
        return;
      }

      if (
        !response.ok &&
        response.message &&
        response.message.includes("already_exists")
      ) {
        setErrors((prev) => ({
          ...(prev ?? {}),
          phone:
            "User already exists with this number, try logging in or forgot password",
        }));
        triggerNotification(
          "User already exists with this number",
          NotificationType.ERROR
        );
        return;
      } else if (response.Code == "103") {
        setErrors((prev) => ({
          ...(prev ?? {}),
          otp: "Invalid otp, please try again",
        }));
        triggerNotification("Invalid OTP, please try again", NotificationType.ERROR);
        return;
      } else if (response?.accessToken && response?.userId) {
        genericUtility.setTokenDetails(
          response.accessToken,
          response.userId,
          response.refreshToken
        );
        triggerNotification("Account created successfully", NotificationType.SUCCESS);
      } else {
        setErrors((prev) => ({
          ...(prev ?? {}),
          password: "Technical issue, please report to support team",
        }));
        triggerNotification(
          "Technical issue, please report to support team",
          NotificationType.ERROR
        );
        return;
      }

      await dispatch(fetchProfile())
        .unwrap()
        .catch((err) => {
          console.warn("fetchProfile failed:", err);
        });

      const returnUrl = genericUtility.getReturnUrl();
      genericUtility.clearReturnUrl();
      navigate(returnUrl || "/home", { replace: true });
      dispatch(closeAuth());
    } catch (error) {
      console.log(`Signup failed due to unknown error ${error}`);
      triggerNotification(
        "Signup failed due to unknown error",
        NotificationType.ERROR
      );
    } finally {
      stopLoading();
    }
  };

  return (
    <div className="signup-container">
      {/* USERNAME FIELD */}
      <div className="field-group">
        <label>
          Username <span className="required-star">*</span>
        </label>
        <input
          type="text"
          autoFocus
          className={errors.username ? "input-error" : ""}
          value={username}
          placeholder="Enter Your Full Name"
          maxLength={15}
          onChange={(e) => {
            setUsername(e.target.value);
            if (errors.username) setErrors({ ...errors, username: "" });
          }}
        />
        {errors.username && <p className="error-text">{errors.username}</p>}
      </div>

      {/* PHONE FIELD */}
      <div className="field-group">
        <label>
          Phone Number <span className="required-star">*</span>
        </label>
        <input
          type="text"
          className={errors.phone ? "input-error" : ""}
          value={phone}
          placeholder="Enter phone number"
          onChange={(e) => {
            setPhone(e.target.value);
            if (errors.phone) setErrors({ ...errors, phone: "" });
          }}
        />
        {errors.phone && <p className="error-text">{errors.phone}</p>}
      </div>

      {/* PASSWORD FIELD */}
      <div className="field-group">
        <label>
          Password <span className="required-star">*</span>
        </label>
        <div className="password-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            className={errors.password ? "input-error" : ""}
            value={password}
            placeholder="Create password"
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) setErrors({ ...errors, password: "" });
            }}
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        {errors.password && <p className="error-text">{errors.password}</p>}
      </div>

      {/* CONFIRM PASSWORD FIELD */}
      <div className="field-group">
        <label>
          Confirm Password <span className="required-star">*</span>
        </label>
        <div className="password-wrapper">
          <input
            type={showConfirmPassword ? "text" : "password"}
            className={errors.confirmPassword ? "input-error" : ""}
            value={confirmPassword}
            placeholder="Re-enter password"
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (errors.confirmPassword)
                setErrors({ ...errors, confirmPassword: "" });
            }}
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="error-text">{errors.confirmPassword}</p>
        )}
      </div>

      <div className="field-group">
        <label>Referral code</label>
        <input
          type="text"
          className={errors.referralCode ? "input-error" : ""}
          value={referralCode}
          placeholder="Eg: instagram / none / mt1"
          onChange={(e) => {
            setReferralCode(e.target.value);
            if (errors.referralCode) setErrors({ ...errors, referralCode: "" });
          }}
        />
        {errors.referralCode && (
          <p className="error-text">{errors.referralCode}</p>
        )}
      </div>

      {/* OTP FIELD (shown only after Get OTP) */}
      {otpSent && (
        <div className="field-group" style={{ animation: "fadeIn 0.3s ease-in-out" }}>
          <label>Enter OTP</label>
          <OtpInput 
            value={otp} 
            onChange={(val: string) => {
              setOtp(val);
              if (errors.otp) setErrors({ ...errors, otp: "" });
            }} 
            error={!!errors.otp} 
            length={6} 
          />
          {errors.otp && <p className="error-text">{errors.otp}</p>}
        </div>
      )}

      {/* WhatsApp OTP Button */}
      <WhatsAppBtn 
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (!otpSent || countdown === 0) handleGetOtp(e as any);
        }}
        disabled={otpSent && countdown > 0}
        otpSent={otpSent}
        countdown={countdown}
      />

      <button
        className="primary-btn"
        onClick={handleCreateAccount}
        disabled={!otpSent}
      >
        Create Account
      </button>
    </div>
  );
};

export default Signup;
